import { PurchaseRepository, CreatePurchaseData, UpdatePurchaseData, GetPurchaseData, PurchaseFilter, GetPurchaseItemData } from '../repository/purchase.repository';
import { CartRepository } from '../repository/cart.repository';

export class PurchaseUseCase {
  constructor(
    private purchaseRepository: PurchaseRepository,
    private cartRepository?: CartRepository
  ) {
    console.log('[PurchaseUseCase] Constructor - Purchase repository initialized:', !!this.purchaseRepository);
  }

  // ===== PURCHASE MANAGEMENT =====

  async createPurchase(purchaseData: CreatePurchaseData): Promise<GetPurchaseData> {
    console.log('[PurchaseUseCase.createPurchase] START - PurchaseData:', purchaseData);
    try {
      // Validate purchase data
      if (!purchaseData.userId) {
        throw new Error('User ID is required');
      }

      if (purchaseData.totalAmount < 0) {
        throw new Error('Total amount cannot be negative');
      }

      if (purchaseData.subtotalAmount < 0) {
        throw new Error('Subtotal amount cannot be negative');
      }

      if (!purchaseData.currency) {
        throw new Error('Currency is required');
      }

      const newPurchase = await this.purchaseRepository.createPurchase(purchaseData);
      console.log('[PurchaseUseCase.createPurchase] SUCCESS - Created purchase:', newPurchase.id);
      return newPurchase;
    } catch (error) {
      console.error('[PurchaseUseCase.createPurchase] ERROR:', error);
      throw error;
    }
  }

  async createPurchaseFromCart(cartId: string, paymentData: {
    payment_method: 'stripe' | 'paypal' | 'apple_pay' | 'google_pay';
    paymentIntentId?: string;
    billingAddress?: Record<string, any>;
    shippingAddress?: Record<string, any>;
  }): Promise<GetPurchaseData> {
    console.log('[PurchaseUseCase.createPurchaseFromCart] START - CartId:', cartId);
    try {
      if (!this.cartRepository) {
        throw new Error('Cart repository not available');
      }

      // Get cart and validate
      const cart = await this.cartRepository.getCartById(cartId);
      if (!cart) {
        throw new Error('Cart not found');
      }

      if (cart.status !== 'active') {
        throw new Error('Can only purchase from active carts');
      }

      const cartItems = await this.cartRepository.getCartItems(cartId);
      if (cartItems.length === 0) {
        throw new Error('Cannot purchase empty cart');
      }

      // Create purchase
      const purchaseData: CreatePurchaseData = {
        userId: cart.userId,
        cartId: cartId,
        payment_method: paymentData.payment_method,
        payment_status: 'pending',
        totalAmount: cart.totalAmount,
        subtotalAmount: cart.totalAmount - (cart.taxAmount || 0) - (cart.shippingAmount || 0) + (cart.discountAmount || 0),
        discountAmount: cart.discountAmount,
        taxAmount: cart.taxAmount,
        shippingAmount: cart.shippingAmount,
        currency: cart.currency,
        paymentIntentId: paymentData.paymentIntentId,
        billingAddress: paymentData.billingAddress,
        shippingAddress: paymentData.shippingAddress,
      };

      const purchase = await this.purchaseRepository.createPurchase(purchaseData);

      // Convert cart items to purchase items
      for (const cartItem of cartItems) {
        await this.purchaseRepository.addPurchaseItem({
          purchaseId: purchase.id,
          productType: cartItem.productType,
          productId: cartItem.productId,
          productName: cartItem.productName,
          quantity: cartItem.quantity,
          unitPrice: cartItem.unitPrice,
          totalPrice: cartItem.totalPrice,
          discountAmount: cartItem.discountAmount,
          metadata: cartItem.metadata,
        });
      }

      // Mark cart as completed
      await this.cartRepository.updateCart(cartId, { status: 'completed' });

      console.log('[PurchaseUseCase.createPurchaseFromCart] SUCCESS - Purchase created from cart');
      return purchase;
    } catch (error) {
      console.error('[PurchaseUseCase.createPurchaseFromCart] ERROR:', error);
      throw error;
    }
  }

  async getPurchaseById(purchaseId: string): Promise<GetPurchaseData | null> {
    console.log('[PurchaseUseCase.getPurchaseById] START - PurchaseId:', purchaseId);
    try {
      if (!purchaseId) {
        throw new Error('Purchase ID is required');
      }

      const purchase = await this.purchaseRepository.getPurchaseById(purchaseId);
      console.log('[PurchaseUseCase.getPurchaseById] SUCCESS - Purchase found:', !!purchase);
      return purchase;
    } catch (error) {
      console.error('[PurchaseUseCase.getPurchaseById] ERROR:', error);
      throw error;
    }
  }

  async getPurchases(filter: PurchaseFilter = {}, limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseUseCase.getPurchases] START - Filter:', filter, 'Limit:', limitCount);
    try {
      const purchases = await this.purchaseRepository.getPurchases(filter, limitCount);
      console.log('[PurchaseUseCase.getPurchases] SUCCESS - Retrieved', purchases.length, 'purchases');
      return purchases;
    } catch (error) {
      console.error('[PurchaseUseCase.getPurchases] ERROR:', error);
      throw error;
    }
  }

  async getAllPurchases(limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseUseCase.getAllPurchases] START - Limit:', limitCount);
    try {
      const purchases = await this.purchaseRepository.getAllPurchases(limitCount);
      console.log('[PurchaseUseCase.getAllPurchases] SUCCESS - Retrieved', purchases.length, 'purchases');
      return purchases;
    } catch (error) {
      console.error('[PurchaseUseCase.getAllPurchases] ERROR:', error);
      throw error;
    }
  }

  async updatePurchase(purchaseId: string, updateData: UpdatePurchaseData): Promise<GetPurchaseData> {
    console.log('[PurchaseUseCase.updatePurchase] START - PurchaseId:', purchaseId, 'UpdateData:', updateData);
    try {
      if (!purchaseId) {
        throw new Error('Purchase ID is required');
      }

      const updatedPurchase = await this.purchaseRepository.updatePurchase(purchaseId, updateData);
      console.log('[PurchaseUseCase.updatePurchase] SUCCESS - Purchase updated');
      return updatedPurchase;
    } catch (error) {
      console.error('[PurchaseUseCase.updatePurchase] ERROR:', error);
      throw error;
    }
  }

  async deletePurchase(purchaseId: string): Promise<void> {
    console.log('[PurchaseUseCase.deletePurchase] START - PurchaseId:', purchaseId);
    try {
      if (!purchaseId) {
        throw new Error('Purchase ID is required');
      }

      // Only allow deletion of failed or pending purchases
      const purchase = await this.purchaseRepository.getPurchaseById(purchaseId);
      if (!purchase) {
        throw new Error('Purchase not found');
      }

      if (purchase.payment_status === 'completed') {
        throw new Error('Cannot delete completed purchases');
      }

      await this.purchaseRepository.deletePurchase(purchaseId);
      console.log('[PurchaseUseCase.deletePurchase] SUCCESS - Purchase deleted');
    } catch (error) {
      console.error('[PurchaseUseCase.deletePurchase] ERROR:', error);
      throw error;
    }
  }

  // ===== USER SPECIFIC OPERATIONS =====

  async getUserPurchases(userId: string, limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseUseCase.getUserPurchases] START - UserId:', userId, 'Limit:', limitCount);
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const purchases = await this.purchaseRepository.getUserPurchases(userId, limitCount);
      console.log('[PurchaseUseCase.getUserPurchases] SUCCESS - Retrieved', purchases.length, 'user purchases');
      return purchases;
    } catch (error) {
      console.error('[PurchaseUseCase.getUserPurchases] ERROR:', error);
      throw error;
    }
  }

  async getUserPurchaseHistory(userId: string, limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseUseCase.getUserPurchaseHistory] START - UserId:', userId);
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const filter: PurchaseFilter = { 
        userId: userId, 
        payment_status: 'completed' 
      };
      const purchases = await this.purchaseRepository.getPurchases(filter, limitCount);
      console.log('[PurchaseUseCase.getUserPurchaseHistory] SUCCESS - Retrieved purchase history');
      return purchases;
    } catch (error) {
      console.error('[PurchaseUseCase.getUserPurchaseHistory] ERROR:', error);
      throw error;
    }
  }

  // ===== PAYMENT STATUS OPERATIONS =====

  async updatePaymentStatus(purchaseId: string, status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded'): Promise<GetPurchaseData> {
    console.log('[PurchaseUseCase.updatePaymentStatus] START - PurchaseId:', purchaseId, 'Status:', status);
    try {
      if (!purchaseId) {
        throw new Error('Purchase ID is required');
      }

      const updatedPurchase = await this.purchaseRepository.updatePurchase(purchaseId, {
        payment_status: status
      });

      console.log('[PurchaseUseCase.updatePaymentStatus] SUCCESS - Payment status updated');
      return updatedPurchase;
    } catch (error) {
      console.error('[PurchaseUseCase.updatePaymentStatus] ERROR:', error);
      throw error;
    }
  }

  async getPurchasesByStatus(status: PurchaseFilter['payment_status'], limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseUseCase.getPurchasesByStatus] START - Status:', status, 'Limit:', limitCount);
    try {
      if (!status) {
        throw new Error('Status is required');
      }

      const purchases = await this.purchaseRepository.getPurchasesByStatus(status, limitCount);
      console.log('[PurchaseUseCase.getPurchasesByStatus] SUCCESS - Retrieved', purchases.length, 'purchases');
      return purchases;
    } catch (error) {
      console.error('[PurchaseUseCase.getPurchasesByStatus] ERROR:', error);
      throw error;
    }
  }

  async completePurchase(purchaseId: string, paymentIntentId?: string): Promise<GetPurchaseData> {
    console.log('[PurchaseUseCase.completePurchase] START - PurchaseId:', purchaseId);
    try {
      if (!purchaseId) {
        throw new Error('Purchase ID is required');
      }

      const purchase = await this.purchaseRepository.getPurchaseById(purchaseId);
      if (!purchase) {
        throw new Error('Purchase not found');
      }

      if (purchase.payment_status === 'completed') {
        console.log('[PurchaseUseCase.completePurchase] Purchase already completed');
        return purchase;
      }

      const updatedPurchase = await this.purchaseRepository.updatePurchase(purchaseId, {
        payment_status: 'completed',
        ...(paymentIntentId && { paymentIntentId: paymentIntentId })
      });

      console.log('[PurchaseUseCase.completePurchase] SUCCESS - Purchase completed');
      return updatedPurchase;
    } catch (error) {
      console.error('[PurchaseUseCase.completePurchase] ERROR:', error);
      throw error;
    }
  }

  async refundPurchase(purchaseId: string, reason?: string): Promise<GetPurchaseData> {
    console.log('[PurchaseUseCase.refundPurchase] START - PurchaseId:', purchaseId, 'Reason:', reason);
    try {
      if (!purchaseId) {
        throw new Error('Purchase ID is required');
      }

      const purchase = await this.purchaseRepository.getPurchaseById(purchaseId);
      if (!purchase) {
        throw new Error('Purchase not found');
      }

      if (purchase.payment_status !== 'completed') {
        throw new Error('Can only refund completed purchases');
      }

      const updatedPurchase = await this.purchaseRepository.updatePurchase(purchaseId, {
        payment_status: 'refunded',
        metadata: {
          ...purchase.metadata,
          refundReason: reason,
          refundedAt: new Date().toISOString()
        }
      });

      console.log('[PurchaseUseCase.refundPurchase] SUCCESS - Purchase refunded');
      return updatedPurchase;
    } catch (error) {
      console.error('[PurchaseUseCase.refundPurchase] ERROR:', error);
      throw error;
    }
  }

  // ===== PURCHASE ITEMS =====

  async getPurchaseItems(purchaseId: string): Promise<GetPurchaseItemData[]> {
    console.log('[PurchaseUseCase.getPurchaseItems] START - PurchaseId:', purchaseId);
    try {
      if (!purchaseId) {
        throw new Error('Purchase ID is required');
      }

      const items = await this.purchaseRepository.getPurchaseItems(purchaseId);
      console.log('[PurchaseUseCase.getPurchaseItems] SUCCESS - Retrieved', items.length, 'items');
      return items;
    } catch (error) {
      console.error('[PurchaseUseCase.getPurchaseItems] ERROR:', error);
      throw error;
    }
  }

  // ===== ANALYTICS =====

  async getRevenueAnalytics(startDate?: Date, endDate?: Date) {
    console.log('[PurchaseUseCase.getRevenueAnalytics] START - DateRange:', startDate, 'to', endDate);
    try {
      const analytics = await this.purchaseRepository.getRevenueAnalytics(startDate, endDate);
      console.log('[PurchaseUseCase.getRevenueAnalytics] SUCCESS - Analytics calculated');
      return analytics;
    } catch (error) {
      console.error('[PurchaseUseCase.getRevenueAnalytics] ERROR:', error);
      throw error;
    }
  }

  async getPurchaseMetrics(userId?: string): Promise<{
    totalSpent: number;
    totalPurchases: number;
    averageOrderValue: number;
    lastPurchaseDate?: Date;
  }> {
    console.log('[PurchaseUseCase.getPurchaseMetrics] START - UserId:', userId);
    try {
      const filter: PurchaseFilter = { 
        payment_status: 'completed',
        ...(userId && { userId: userId })
      };
      
      const purchases = await this.purchaseRepository.getPurchases(filter);
      
      const totalSpent = purchases.reduce((sum, p) => sum + p.totalAmount, 0);
      const totalPurchases = purchases.length;
      const averageOrderValue = totalPurchases > 0 ? totalSpent / totalPurchases : 0;
      const lastPurchaseDate = purchases.length > 0 ? purchases[0].createdAt.toDate() : undefined;

      const metrics = {
        totalSpent,
        totalPurchases,
        averageOrderValue,
        lastPurchaseDate
      };

      console.log('[PurchaseUseCase.getPurchaseMetrics] SUCCESS - Metrics calculated');
      return metrics;
    } catch (error) {
      console.error('[PurchaseUseCase.getPurchaseMetrics] ERROR:', error);
      throw error;
    }
  }
}